import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useUser } from "@/shared/auth/useUser";
import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DeleteClusterConfirm } from "@/shared/ui/DeleteClusterConfirm";
import { DangerZone } from "@/shared/ui/DangerZone";
import { EmptyState } from "@/shared/ui/EmptyState";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import { humanizeTime, humanizeBytes } from "@/shared/lib/format";
import {
  useGetCluster,
  useNodes,
  useCapabilities,
  useTestClusterQuery,
  useClusterBuckets,
  useClusterKeys,
  useBucket,
  useKey,
  useClusterAdmins,
  useAssignRole,
  useUnassignRole,
  usePolicies,
  useClusterLockStatus,
  useLockCluster,
  useAddClusterAdmin,
  useRemoveClusterAdmin,
  type ClusterAdminAssignment,
  type PolicyRole,
} from "@/shared/api/queries";
import { LockBadge } from "@/shared/ui/LockBadge";
import { useClusterUnlockPrompt } from "@/shared/auth/clusterUnlock";
import { client } from "@/shared/api/client";
import { useDeleteCluster } from "@/shared/api/mutations";
import { useCan } from "@/shared/auth/useCan";
import { CAP } from "@/shared/auth/capabilities";
import { adminPage } from "@/shared/layout/adminPage";
import type { components } from "@/shared/api/types.gen";
import { DriverBadge } from "@/components/clusters/DriverBadge";
import { useElevationGuard } from "@/shared/auth/elevation";

export const Route = createFileRoute("/admin/clusters/$cid/")({
  component: adminPage(ClusterDetailScreen),
});

function ClusterDetailScreen() {
  const { cid } = Route.useParams();
  const navigate = useNavigate();
  useUser();
  const { can } = useCan();
  const { t } = useTranslation("pages");

  // ADR-0009 wiring-vs-contents split. The page renders the WIRING
  // surface (label/driver/health/nodes/test/edit/delete) for anyone
  // with cluster.wiring.read — that's UI Admin (any cluster) AND
  // cluster-admin (their cluster). The CONTENTS surface
  // (buckets/keys/encryption-admins/lock-status) belongs to
  // cluster-admin only (cluster.contents.read); a UI Admin holds
  // wiring.read but NOT contents.read, so we must NOT fire the contents
  // queries for them or the page lights up with 403 toasts.
  const canContents = can(CAP.CLUSTER_CONTENTS_READ);
  // "Back to Clusters" only makes sense for someone who can see the
  // cross-cluster list (cluster.wiring.list = UI Admin). A cluster-
  // admin has a single cluster and no list page, so hide it for them.
  const hasClusterList = can(CAP.CLUSTER_WIRING_LIST);
  // Edit/migrate/delete are wiring operations (UI Admin only). A
  // cluster-admin reads the wiring surface but cannot mutate the
  // connection, so hide these write affordances for them.
  const canWiringWrite = can(CAP.CLUSTER_WIRING_UPDATE);
  const canWiringDelete = can(CAP.CLUSTER_WIRING_DELETE);

  const { data: cluster, isLoading, error } = useGetCluster(cid);
  const { data: nodes } = useNodes(cid, canContents);
  const { data: capabilities } = useCapabilities();
  const { data: buckets, isLoading: bucketsLoading } = useClusterBuckets(cid, canContents);
  const { data: keys, isLoading: keysLoading } = useClusterKeys(cid, canContents);
  const { data: lockStatus } = useClusterLockStatus(cid, canContents);
  const deleteCluster = useDeleteCluster();
  // ADR-0003 v1.2.0b: cluster:delete is ELEVATED-min — wrap the
  // click handler so a 403 ELEVATION_REQUIRED triggers the password
  // modal and retries the delete on success.
  const runWithElevation = useElevationGuard();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Test cluster state — auto-fires on mount + caches 60s so the
  // header HealthPill reflects reality, not a default 'Unavailable'.
  const testQuery = useTestClusterQuery(cid, { auto: true });
  const testResult = testQuery.data ?? null;

  const handleTestCluster = () => {
    // useTestClusterQuery polls automatically; manual trigger is just
    // a refetch.
    testQuery.refetch();
  };

  const getStatusFromResult = (result?: components["schemas"]["ConnectionTestResult"]) => {
    if (!result?.ok) return "unavailable";
    if (result.message?.toLowerCase().includes("degraded")) return "degraded";
    return "healthy";
  };

  const status: "healthy" | "degraded" | "unavailable" | "checking" =
    testResult ? getStatusFromResult(testResult)
    : (testQuery.isFetching || testQuery.isPending) ? "checking"
    : "unavailable";

  if (error) {
    return (
      <div className="space-y-6">
        <BackLink hideBack={!hasClusterList} />
        <ErrorBanner message={t("errors.connectionFailed")} />
      </div>
    );
  }

  if (isLoading || !cluster) {
    return (
      <div className="space-y-6">
        <BackLink hideBack={!hasClusterList} />
        <Skeleton className="h-8 w-48" />
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-20 w-full rounded-lg" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <BackLink hideBack={!hasClusterList} />

      {/* Header */}
      <div className="space-y-2">
        <div className="min-w-0 flex flex-wrap items-center gap-3">
          {colorDot(cluster.color)}
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{cluster.label}</h1>
          <DriverBadge driver={cluster.driver} />
          <HealthPill status={status} message={testResult?.message} />
          {lockStatus ? (
            <LockBadge
              unlocked={lockStatus.unlocked}
              hasCsk={lockStatus.hasCsk}
              requiresMigration={lockStatus.requiresMigration}
            />
          ) : null}
          <div className="flex-1 min-w-0" />
          {canWiringWrite && (
            <div className="flex flex-wrap gap-2 flex-shrink-0">
               <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate({ to: "/admin/migrate", search: { srcCid: cid } })}
                  title={t("adminClustersDetail.migrateThisClusterTitle")}
                >
                  {t("adminClustersDetail.migrateThisCluster")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate({ to: "/admin/clusters/$cid/edit", params: { cid } })}
                  title={t("adminClustersDetail.editClusterTitle")}
                >
                  {t("adminClustersDetail.editCluster")}
                </Button>
            </div>
          )}
        </div>
      </div>

      {/* Stats card */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* ADR-0009: bucket/key counts AND the nodes count are all
                contents facts — the backend gates the buckets/keys/nodes
                endpoints on cluster.contents.read, so only show these to
                a viewer who holds it. A UI Admin (wiring.read only) sees
                the wiring surface (label/driver/health/test/edit) but
                none of these counts, and fires none of their queries. */}
            {canContents && (
              <>
                <div className="text-center p-4 rounded-lg bg-muted/50">
                  <div className="text-xl font-semibold tabular-nums">
                    {bucketsLoading ? <Skeleton className="h-6 w-10 mx-auto" /> : (buckets?.length ?? 0)}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{t("adminClustersList.bucketCount")}</div>
                </div>
                <div className="text-center p-4 rounded-lg bg-muted/50">
                  <div className="text-xl font-semibold tabular-nums">
                    {keysLoading ? <Skeleton className="h-6 w-10 mx-auto" /> : (keys?.length ?? 0)}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{t("adminClustersList.keyCount")}</div>
                </div>
                <div className="text-center p-4 rounded-lg bg-muted/50">
                  <div className="text-xl font-semibold tabular-nums">{nodes?.length ?? 0}</div>
                  <div className="text-xs text-muted-foreground mt-1">{t("adminClustersDetail.nodesTitle")}</div>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Test Connection */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <span>{t("adminClustersDetail.testConnection")}</span>
          <Button 
            size="sm" 
            variant="outline"
            onClick={handleTestCluster}
            disabled={testQuery.isPending}
          >
            {testQuery.isPending ? t("adminClustersDetail.testing") : t("adminClustersDetail.testConnection")}
          </Button>
        </CardHeader>
        <CardContent className="pt-6">
          {testResult ? (
            testResult.code === "MISSING_ADMIN_TOKEN" ? (
              <div className="rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-4">
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  {t("adminClustersDetail.missingAdminTokenBanner")}{" "}
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 h-auto text-yellow-700 underline dark:text-yellow-300"
                    onClick={() => navigate({ to: "/admin/clusters/$cid/edit", params: { cid } })}
                  >
                    {t("adminClustersDetail.editCluster")}
                  </Button>
                </p>
              </div>
            ) : (
              <div className={`text-sm ${testResult.ok ? "text-green-600" : "text-destructive"}`}>
                {testResult.ok ? t("adminClustersDetail.connectionHealthy") : `${t("adminClustersDetail.connectionUnavailable").replace("{message}", testResult.message || "")}`}
              </div>
            )
          ) : (testQuery.isFetching || testQuery.isPending) ? (
            <p className="text-sm text-muted-foreground">{t("adminClustersDetail.testing")}…</p>
          ) : (
            <p className="text-sm text-muted-foreground">Click "{t("adminClustersDetail.testConnection")}" to verify the cluster is reachable.</p>
          )}
        </CardContent>
      </Card>

    {/* ADR-0009: the CONTENTS surface (admins / encryption / buckets /
          keys) only renders for cluster.contents.read — cluster-admin
          on their cluster. A UI Admin holds wiring.read but NOT
          contents.read, so these sections (and their queries, which
          were disabled above) stay hidden rather than 403. */}
      {canContents && (
        <>
    {/* Cluster admins — persona-level info, shown above Buckets
          because "who runs this cluster" is the operator's first
          question when they hit a cluster detail page. v1.3.0e. */}
       <ClusterAdminsSection cid={cid} t={t} />

     {/* v1.12.0a (ADR-0007) — per-cluster envelope encryption.
          Lives next to the persona-level admin list because the
          two questions ("who runs this cluster" + "who can unlock
          its secrets") are operationally adjacent. */}
       <ClusterEncryptionSection cid={cid} clusterLabel={cluster.label} t={t} />

      {/* Buckets section — admin-grade columns */}
      <section className="space-y-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-sm font-medium text-muted-foreground">
            {t("adminClustersDetail.bucketsTitle")}
            {buckets ? <span className="ml-1.5 text-muted-foreground/60">({buckets.length})</span> : null}
          </h2>
        </div>
        {bucketsLoading ? (
          <Skeleton className="h-24 w-full rounded-lg" />
        ) : !buckets || buckets.length === 0 ? (
          <div className="rounded-lg border bg-card p-6">
            <EmptyState
              icon="database"
              title={t("adminClustersDetail.noBucketsYet")}
              description={t("adminClustersDetail.bucketsAppearHere")}
            />
          </div>
        ) : (
          <div className="rounded-lg border bg-card overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                 <TableHeader>
                    <TableRow>
                      <TableHead>{t("adminClustersDetail.nameColumn")}</TableHead>
                      <TableHead className="text-right w-[140px] sm:hidden">{t("adminClustersDetail.sizeColumn")}</TableHead>
                      <TableHead className="text-right w-[120px] sm:hidden">{t("adminClustersDetail.objectsColumn")}</TableHead>
                    </TableRow>
                  </TableHeader>
                <TableBody>
                  {buckets.slice(0, 8).map((b) => (
                    <ClusterBucketRow key={b.id} cid={cid} bucketId={b.id} fallbackAlias={b.aliases?.[0]} />
                  ))}
                </TableBody>
              </Table>
            </div>
            {buckets.length > 8 && (
              <div className="px-4 py-2 text-xs text-muted-foreground border-t">
                + {buckets.length - 8} more in this cluster
              </div>
            )}
          </div>
        )}
      </section>

     {/* Keys section — admin-grade columns */}
      <section className="space-y-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-sm font-medium text-muted-foreground">
            {t("adminClustersDetail.keysTitle")}
            {keys ? <span className="ml-1.5 text-muted-foreground/60">({keys.length})</span> : null}
          </h2>
        </div>
        {keysLoading ? (
          <Skeleton className="h-24 w-full rounded-lg" />
        ) : !keys || keys.length === 0 ? (
          <div className="rounded-lg border bg-card p-6">
            <EmptyState
              icon="key"
              title={t("adminClustersDetail.noKeysYet")}
              description={t("adminClustersDetail.keysAppearHere")}
            />
          </div>
        ) : (
          <div className="rounded-lg border bg-card overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                    <TableRow>
                      <TableHead>{t("adminClustersDetail.nameColumn")}</TableHead>
                      <TableHead className="sm:hidden">{t("adminClustersDetail.accessKeyIdColumn")}</TableHead>
                    </TableRow>
                  </TableHeader>
                <TableBody>
                  {keys.slice(0, 8).map((k) => (
                    <ClusterKeyRow key={k.id} cid={cid} keyId={k.id} fallbackName={k.name} />
                  ))}
                </TableBody>
              </Table>
            </div>
            {keys.length > 8 && (
              <div className="px-4 py-2 text-xs text-muted-foreground border-t">
                {/* v1.11.0.15: no link target — the global aggregate
                    page was removed and keys are inherently
                    per-cluster. Tail count only. */}
                + {keys.length - 8} more on this cluster
              </div>
            )}
          </div>
        )}
      </section>
        </>
      )}

      {/* Nodes section — ADR-0009: nodes is a contents fact (the
          endpoint is contents.read-gated), so hide it from a UI Admin;
          the layout capability further hides it in readonly mode. */}
      {canContents && capabilities?.layout !== "readonly" && (
        <Card>
          <CardHeader>{t("adminClustersDetail.nodesTitle")}</CardHeader>
          <CardContent className="pt-6">
            {!nodes || nodes.length === 0 ? (
              <EmptyState
                icon="server"
                title={t("adminClustersDetail.noNodesConfigured")}
                description={t("adminClustersDetail.addNodesViaLayout")}
              />
            ) : (
              <div className="overflow-x-auto">
                <Table>
                 <TableHeader>
                      <TableRow>
                        <TableHead className="w-1/4 sm:hidden">{t("adminClustersDetail.idColumn")}</TableHead>
                        <TableHead className="sm:hidden">{t("adminClustersDetail.hostnameColumn")}</TableHead>
                        <TableHead className="sm:hidden">{t("adminClustersDetail.roleColumn")}</TableHead>
                        <TableHead className="sm:hidden">{t("adminClustersDetail.zoneColumn")}</TableHead>
                        <TableHead>{t("adminClustersDetail.statusColumn")}</TableHead>
                      </TableRow>
                    </TableHeader>
                  <TableBody>
                    {nodes.map((node) => (
                      <TableRow key={node.id}>
                        <TableCell className="font-mono text-xs sm:hidden">{node.id.slice(0, 12)}</TableCell>
                        <TableCell className="sm:hidden">{node.hostname ?? "—"}</TableCell>
                        <TableCell className="sm:hidden">{node.role ?? "unassigned"}</TableCell>
                        <TableCell className="sm:hidden">{node.zone ?? "—"}</TableCell>
                        <TableCell><NodeStatus status={node.status} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

    {/* ADR-0009: Scrub + Layout are cluster-admin maintenance ops
          (cluster.scrub.write / cluster.layout.write). UI Admin holds
          neither, so hide these for anyone without contents access —
          the route gate would bounce them anyway. */}
      {canContents && (
        <>
    {/* Maintenance → Scrub link (v1.4.0c). Always rendered — the
          page itself surfaces "not supported" for AWS/MinIO drivers
          rather than hiding the link, so an operator can always learn
          why scrub isn't available on a backend instead of finding
          it missing without explanation. */}
       <Link to="/admin/clusters/$cid/scrub" params={{ cid }}>
         <Card className="cursor-pointer hover:bg-muted/50 transition-colors">
           <CardContent className="pt-6">
             <div className="flex items-center justify-between">
               <div>
                 <h2 className="font-medium">{t("adminClustersDetail.maintenanceScrubTitle")}</h2>
                 <p className="text-sm text-muted-foreground mt-1">
                   {t("adminClustersDetail.maintenanceScrubDescription")}
                 </p>
               </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-5 w-5 text-muted-foreground"
              >
                <path d="m9 18 6-6-6-6" />
              </svg>
            </div>
          </CardContent>
        </Card>
      </Link>

   {/* Layout section - gated by capability. Route under cluster
          scope lands with CLUSTER.LAYOUT-EDITOR; for now anchor. */}
       {capabilities?.layout !== "readonly" ? (
         <Link to="/admin/clusters/$cid/layout" params={{ cid }}>
           <Card className="cursor-pointer hover:bg-muted/50 transition-colors">
             <CardContent className="pt-6">
               <div className="flex items-center justify-between">
                 <div>
                   <h2 className="font-medium">{t("adminClustersDetail.layoutTitle")}</h2>
                   <p className="text-sm text-muted-foreground mt-1">
                     {t("adminClustersDetail.editTopology")}
                   </p>
                 </div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-5 w-5 text-muted-foreground"
                >
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </div>
            </CardContent>
          </Card>
        </Link>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <h2 className="font-medium mb-2">{t("adminClustersDetail.layoutTitle")}</h2>
            <p className="text-sm text-muted-foreground">
              {t("adminClustersDetail.layoutNotSupported")}
            </p>
          </CardContent>
        </Card>
      )}
        </>
      )}

      {/* Created */}
      {cluster.createdAt && humanizeTime(cluster.createdAt) !== "—" && (
        <p className="text-xs text-muted-foreground">{t("adminClustersDetail.createdLabel")} {humanizeTime(cluster.createdAt)}</p>
      )}

      {/* Danger Zone — cluster delete is a wiring op (UI Admin). */}
      {canWiringDelete && (
        <DangerZone description={t("adminClustersDetail.deleteDescription")}>
          <Button
            variant="destructive"
            onClick={() => setDeleteDialogOpen(true)}
            disabled={testQuery.isPending}
          >
            {t("adminClustersDetail.deleteClusterButton")}
          </Button>
        </DangerZone>
      )}

      <DeleteClusterConfirm
        open={deleteDialogOpen}
        clusterLabel={cluster.label}
        isDeleting={deleteCluster.isPending}
        onConfirm={async () => {
          setDeleteDialogOpen(false);
          try {
            await runWithElevation(() => deleteCluster.mutateAsync(cid));
          } catch {
            // ELEVATION_CANCELLED / network errors surface via the
            // mutation's existing error toast / banner.
          }
        }}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </div>
  );
}

/** Cluster-detail bucket row. Fires its own useBucket() so we get
 *  size/objects/created (the cluster-scoped list endpoint only
 *  returns id + aliases on Garage v1). Eight max per cluster-detail
 *  page so the parallel fetch fan-out is bounded. */
function ClusterBucketRow({ cid, bucketId, fallbackAlias }: { cid: string; bucketId: string; fallbackAlias?: string }) {
  const { data: detail } = useBucket(cid, bucketId);
  const name = detail?.aliases?.[0] ?? fallbackAlias ?? bucketId.slice(0, 12);
  return (
    <TableRow
      className="cursor-pointer hover:bg-muted/40"
      onClick={(e) => {
        const navTarget = (e.currentTarget.querySelector("a[data-row-link]") as HTMLAnchorElement | null);
        if (navTarget) navTarget.click();
      }}
    >
      <TableCell className="font-medium">
        <Link
          to="/admin/clusters/$cid/buckets/$id"
          params={{ cid, id: bucketId }}
          data-row-link
          className="hover:underline"
        >
          {name}
        </Link>
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {detail ? humanizeBytes(detail.bytes) : <Skeleton className="h-3 w-12 ml-auto" />}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {detail ? (detail.objects ?? 0).toLocaleString() : <Skeleton className="h-3 w-8 ml-auto" />}
      </TableCell>
    </TableRow>
  );
}

/** Cluster-detail key row. Fires useKey() to pull the canonical
 *  access-key-ID (Garage stores ID-as-access-key but the field on
 *  the list response is just `id`; the detail response carries the
 *  separate accessKeyId field if it differs). */
function ClusterKeyRow({ cid, keyId, fallbackName }: { cid: string; keyId: string; fallbackName?: string }) {
  const { data: detail } = useKey(cid, keyId);
  const name = detail?.name || fallbackName || "Unnamed";
  const akid = detail?.accessKeyId ?? keyId;
  return (
    <TableRow
      className="cursor-pointer hover:bg-muted/40"
      onClick={(e) => {
        const navTarget = (e.currentTarget.querySelector("a[data-row-link]") as HTMLAnchorElement | null);
        if (navTarget) navTarget.click();
      }}
    >
      <TableCell className="font-medium">
        <Link
          to="/admin/clusters/$cid/keys/$id"
          params={{ cid, id: keyId }}
          data-row-link
          className="hover:underline"
        >
          {name}
        </Link>
      </TableCell>
      <TableCell className="font-mono text-xs text-muted-foreground">{akid}</TableCell>
    </TableRow>
  );
}

function BackLink({ hideBack }: { hideBack?: boolean }) {
  if (hideBack) return null;
  return (
    <Link
      to="/admin/clusters"
      className="inline-flex items-center gap-1 text-sm font-medium hover:underline text-muted-foreground"
    >
      ← Back to Clusters
    </Link>
  );
}

function colorDot(color?: string) {
  return (
    <span
      className="inline-block h-3 w-3 rounded-full"
      style={{ backgroundColor: color ?? "#C9874B" }}
      aria-label={`Cluster color`}
    />
  );
}

function HealthPill({ status, message }: { status: "healthy" | "degraded" | "unavailable" | "checking"; message?: string }) {
  const variants = {
    healthy: "bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20",
    degraded: "bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20",
    unavailable: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20",
    checking: "bg-muted/50 text-muted-foreground border-border",
  } as const;

  const labels = {
    healthy: "Healthy",
    degraded: "Degraded",
    unavailable: "Unavailable",
    checking: "Checking…",
  } as const;

  return (
    <TooltipWrapper message={message}>
      <Badge variant="outline" className={`${variants[status]} px-3 py-1`}>
        {labels[status]}
      </Badge>
    </TooltipWrapper>
  );
}

function TooltipWrapper({ message, children }: { message?: string; children: React.ReactNode }) {
  if (!message) return <>{children}</>;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent>
          <p>{message}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function NodeStatus({ status }: { status?: string }) {
  const isLive = status === "connected";
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${isLive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
      {status ?? "unknown"}
    </span>
  );
}

// --- Cluster admins section (v1.3.0e CLUSTER.ADMINS) ----------------
//
// Contextual view of who has admin authority over THIS cluster.
// Reads from /admin/clusters/{cid}/admins which joins user display
// names server-side and marks wildcard-inherited rows. Writes go
// through the global assignment endpoints with scope cluster:{cid}.
//
// Inherited rows (cluster:* or * superuser) render an "inherited
// from global" badge and disable the Remove button — those have to
// be managed from /admin/policies because they affect more than this
// cluster.
function ClusterAdminsSection({ cid, t }: { cid: string; t: ReturnType<typeof useTranslation>["t"] }) {
  const { data, isLoading, error } = useClusterAdmins(cid);
  const [addOpen, setAddOpen] = useState(false);

  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-medium text-muted-foreground">
          {t("adminClustersDetail.clusterAdminsTitle")}
          {data?.assignments ? (
            <span className="ml-1.5 text-muted-foreground/60">
              ({data.assignments.length})
            </span>
          ) : null}
        </h2>
        <Button size="sm" variant="outline" onClick={() => setAddOpen(true)}>
          {t("adminClustersDetail.+AddClusterAdmin")}
        </Button>
      </div>

      {error ? (
        <ErrorBanner message={t("adminClustersDetail.clusterAdminsLoadFailed") || "Couldn't load cluster admins."} />
      ) : isLoading ? (
        <Skeleton className="h-24 w-full rounded-lg" />
      ) : !data || data.assignments.length === 0 ? (
        <div className="rounded-lg border bg-card p-6">
          <EmptyState
            icon="key"
            title={t("adminClustersDetail.noClusterAdminsAssigned")}
            description={t("adminClustersDetail.adminControlsCluster")}
          />
        </div>
      ) : (
        <div className="rounded-lg border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("adminClustersDetail.userColumn")}</TableHead>
                  <TableHead className="w-[140px] sm:hidden">{t("adminClustersDetail.roleColumn")}</TableHead>
                  <TableHead className="w-[180px] sm:hidden">{t("adminClustersDetail.sourceColumn")}</TableHead>
                  <TableHead className="w-[120px] text-right sm:hidden">{t("adminClustersDetail.actionsColumn")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.assignments.map((a, i) => (
                   <ClusterAdminRow
                     key={`${a.userId}|${a.roleId}|${a.scope}|${i}`}
                     cid={cid}
                     assignment={a}
                     t={t}
                   />
                 ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {addOpen && (
        <AddClusterAdminDialog
          cid={cid}
          onClose={() => setAddOpen(false)}
          t={t}
        />
      )}
    </section>
  );
}

// Exported for unit tests in __tests__/cluster-admins-row.test.tsx.
export function ClusterAdminRow({
  cid,
  assignment,
  t,
}: {
  cid: string;
  assignment: ClusterAdminAssignment;
  t: ReturnType<typeof useTranslation>["t"];
}) {
  const queryClient = useQueryClient();
  const unassign = useUnassignRole();
  const runWithElevation = useElevationGuard();

  const displayLabel = assignment.displayName?.trim()
    ? `${assignment.displayName} (${assignment.userId})`
    : assignment.userId;

  // Source column: combine the policy-source field (manual/oidc) with
  // the inherited flag. Inherited rows render the badge regardless of
  // whether they were originally manual or oidc — for the operator
  // looking at this view, "inherited from global" is the
  // load-bearing fact.
  let sourceLabel: string;
  let sourceBadgeClass = "text-muted-foreground border-muted-foreground/30";
  if (assignment.inherited) {
    sourceLabel = t("adminClustersDetail.inheritedFromGlobal");
    sourceBadgeClass =
      "text-amber-700 dark:text-amber-400 border-amber-500/40 bg-amber-500/10";
  } else if (assignment.source === "oidc") {
    sourceLabel = t("adminClustersDetail.oidcSource");
  } else {
    sourceLabel = t("adminClustersDetail.manualSource");
  }

  const handleRevoke = async () => {
    if (
      !confirm(
        t("adminClustersDetail.revokeConfirm", { roleId: assignment.roleId, userId: assignment.userId }),
      )
    ) {
      return;
    }
    try {
      await runWithElevation(() =>
        unassign.mutateAsync({
          userId: assignment.userId,
          roleId: assignment.roleId,
          scope: assignment.scope,
        }),
      );
      queryClient.invalidateQueries({
        queryKey: ["admin", "clusters", cid, "admins"],
      });
      // Also refresh /admin/policies cache so the global matrix
      // sees the change without a full reload.
      queryClient.invalidateQueries({ queryKey: ["admin", "policies"] });
      toast.success(t("adminClustersDetail.clusterDeletedSuccess"));
    } catch (e) {
      if ((e as Error)?.message === "ELEVATION_CANCELLED") return;
      toast.error((e as Error).message);
    }
  };

  return (
    <TableRow>
      <TableCell className="font-medium">{displayLabel}</TableCell>
      <TableCell>
        <Badge variant="outline" className="font-mono text-[10px]">
          {assignment.roleId}
        </Badge>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className={`font-mono text-[10px] ${sourceBadgeClass}`}>
          {sourceLabel}
        </Badge>
      </TableCell>
      <TableCell className="text-right">
        {assignment.inherited ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button variant="destructive" size="sm" disabled>
                  {t("adminClustersDetail.removeButton")}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              {t("adminClustersDetail.assignmentInherited", { scope: assignment.scope })}
            </TooltipContent>
          </Tooltip>
        ) : (
          <Button
            variant="destructive"
            size="sm"
            onClick={handleRevoke}
            disabled={unassign.isPending}
          >
            {t("adminClustersDetail.removeButton")}
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

// AddClusterAdminDialog — modal with two fields (user + role) per
// the popups-max-2-fields doctrine. Scope is implicit from the
// route param so it doesn't count as a field. User is a dropdown
// of all users from /admin/users (eagerly fetched on dialog open);
// role defaults to `cluster_admin` and offers any non-deprecated
// role from the policy matrix.
function AddClusterAdminDialog({
  cid,
  onClose,
  t,
}: {
  cid: string;
  onClose: () => void;
  t: ReturnType<typeof useTranslation>["t"];
}) {
  const queryClient = useQueryClient();
  const assign = useAssignRole();
  const runWithElevation = useElevationGuard();
  const { data: policies } = usePolicies();

  // Eagerly load the users list so the dropdown has labels. The
  // /admin/users endpoint is uiAdmin-gated, which the operator
  // viewing this page already passes (cluster-detail is admin-only).
  const [users, setUsers] = useState<Array<{ username: string; name?: string }>>(
    [],
  );
  const [usersError, setUsersError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await client.GET("/admin/users");
        if (cancelled) return;
        if (error || !data) {
          setUsersError("Couldn't load users");
          return;
        }
        setUsers(
          (data as Array<{ username: string; name?: string }>).map((u) => ({
            username: u.username,
            name: u.name,
          })),
        );
      } catch {
        if (!cancelled) setUsersError("Couldn't load users");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const availableRoles: PolicyRole[] = (policies?.roles ?? []).filter(
    (r) => !r.deprecated,
  );
  const defaultRoleId =
    availableRoles.find((r) => r.id === "cluster_admin")?.id ??
    availableRoles[0]?.id ??
    "";

  const [userId, setUserId] = useState("");
  const [roleId, setRoleId] = useState(defaultRoleId);

  // Re-sync the role default once the policies query lands (it's
  // empty on first render).
  useEffect(() => {
    if (!roleId && defaultRoleId) setRoleId(defaultRoleId);
  }, [defaultRoleId, roleId]);

  const handleAssign = async () => {
    if (!userId.trim() || !roleId) {
      toast.error(t("adminClustersDetail.pickUserAndRole"));
      return;
    }
    try {
      await runWithElevation(() =>
        assign.mutateAsync({
          userId: userId.trim(),
          roleId,
          scope: `cluster:${cid}`,
        }),
      );
      queryClient.invalidateQueries({
        queryKey: ["admin", "clusters", cid, "admins"],
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "policies"] });
      toast.success(t("adminClustersDetail.clusterAdminAssignedSuccess", { roleId, userId }));
      onClose();
    } catch (e) {
      if ((e as Error)?.message === "ELEVATION_CANCELLED") return;
      toast.error((e as Error).message);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogHeader>
        <DialogTitle>{t("adminClustersDetail.+AddClusterAdminDialog")}</DialogTitle>
        <p className="text-sm text-muted-foreground">
          {t("adminClustersDetail.grantAdminRights", { cid })}
        </p>
      </DialogHeader>

      <div className="space-y-3">
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">
            {t("adminClustersDetail.userLabel")}
          </label>
          {usersError ? (
            <div className="space-y-1">
              <Input
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder={t("adminClustersDetail.usernamePlaceholder") || "Enter username"}
              />
              <p className="text-xs text-destructive">{usersError} — {t("adminClustersDetail.enterManually") || "enter manually"}</p>
            </div>
          ) : users.length === 0 ? (
            <Skeleton className="h-8 w-full" />
          ) : (
            <select
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              className="h-8 w-full rounded-lg border border-input bg-transparent px-2 text-sm"
            >
              <option value="">{t("adminClustersDetail.pickUserPlaceholder")}</option>
              {users.map((u) => (
                <option key={u.username} value={u.username}>
                  {u.name ? `${u.name} (${u.username})` : u.username}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">
            {t("adminClustersDetail.roleLabel")}
          </label>
          <select
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
            className="h-8 w-full rounded-lg border border-input bg-transparent px-2 text-sm"
          >
            {availableRoles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label || r.id}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground">
            {t("adminClustersDetail.mostOperatorsWant")}
          </p>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={assign.isPending}>
          {t("adminClustersDetail.cancelButton")}
        </Button>
        <Button onClick={handleAssign} disabled={assign.isPending}>
          {assign.isPending ? t("adminClustersDetail.AssigningLabel") : t("adminClustersDetail.assignButton")}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// ─── v1.12.0a (ADR-0007) ─────────────────────────────────────────────
// ClusterEncryptionSection: per-cluster envelope encryption pane.
// Three states:
//   1. No CSK yet → "Enable encryption" CTA opens the bootstrap dialog.
//   2. CSK + unlocked → list admins, "Add admin" / "Remove" actions,
//      "Lock cluster" button.
//   3. CSK + locked → "Unlock cluster" button (opens the unlock modal).
function ClusterEncryptionSection({
  cid,
  clusterLabel,
  t,
}: {
  cid: string;
  clusterLabel: string;
  t: ReturnType<typeof useTranslation>["t"];
}) {
  const { data: status, isLoading } = useClusterLockStatus(cid);
  const promptUnlock = useClusterUnlockPrompt();
  const lock = useLockCluster(cid);
  const addAdmin = useAddClusterAdmin(cid);
  const removeAdmin = useRemoveClusterAdmin(cid);

  const [addOpen, setAddOpen] = useState(false);
  const [bootstrapOpen, setBootstrapOpen] = useState(false);

  if (isLoading || !status) {
    return (
      <section className="space-y-3">
        <h2 className="text-sm font-medium text-muted-foreground">Envelope encryption</h2>
        <Skeleton className="h-20 w-full rounded-lg" />
      </section>
    );
  }

  const handleLock = () => {
    lock.mutate(undefined, {
      onSuccess: () => toast.success(t("adminClustersDetail.clusterLocked")),
      onError: (e) => toast.error(e.message || t("adminClustersDetail.lockFailed") || "Lock failed"),
    });
  };

  const handleUnlock = () => {
    promptUnlock(cid, clusterLabel).catch(() => {
      // User cancelled — modal closes, no toast needed.
    });
  };

  const handleRemove = (adminUserId: string) => {
    const ok = window.confirm(
      t("adminClustersDetail.removeAdminConfirm", { adminUserId }),
    );
    if (!ok) return;
    removeAdmin.mutate(
      { adminUserId },
      {
        onSuccess: () => toast.success(t("adminClustersDetail.adminRemoved", { adminUserId })),
        onError: (e) => toast.error(e.message || t("adminClustersDetail.removeFailed") || "Remove failed"),
      },
    );
  };

  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-medium text-muted-foreground">{t("adminClustersDetail.EnvelopeEncryptionTitle")}</h2>
        <span className="text-xs text-muted-foreground">{t("adminClustersDetail.ADR0007Reference")}</span>
      </div>
      <Card>
        <CardContent className="pt-6 space-y-4">
          {!status.hasCsk ? (
            <div className="space-y-3">
              <p className="text-sm">{t("adminClustersDetail.unencryptedClusterCopy")}</p>
              <Button size="sm" onClick={() => setBootstrapOpen(true)}>
                {t("adminClustersDetail.enableEncryptionButton")}
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 text-sm">
                <span className="text-muted-foreground">{t("adminClustersDetail.statusLabel")}</span>
                {status.unlocked ? (
                  <span className="text-green-700">{t("adminClustersDetail.secretsAccessible")}</span>
                ) : (
                  <span className="text-red-700">{t("adminClustersDetail.mutationsPromptPassword")}</span>
                )}
              </div>

              {status.requiresMigration ? (
                <p className="text-xs rounded border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900">
                  {t("adminClustersDetail.legacyMigrationNotice")}
                </p>
              ) : null}

              <div className="space-y-2">
                <div className="text-xs text-muted-foreground">
                  {t("adminClustersDetail.adminsSectionLabel")} ({status.admins.length})
                </div>
                <ul className="space-y-1">
                  {status.admins.map((u) => (
                   <li key={u} className="flex items-center justify-between text-sm">
                        <span className="font-mono">{u}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleRemove(u)}
                          disabled={removeAdmin.isPending}
                        >
                          {t("adminClustersDetail.remove")}
                        </Button>
                      </li>
                  ))}
                </ul>
              </div>

              <div className="flex gap-2 pt-2">
                {status.unlocked ? (
                  <>
                    <Button size="sm" variant="outline" onClick={handleLock} disabled={lock.isPending}>
                      {lock.isPending ? t("adminClustersDetail.LockingLabel") : t("adminClustersDetail.lockClusterButton")}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setAddOpen(true)}>
                      {t("adminClustersDetail.AddAdminDialog")}
                    </Button>
                  </>
                ) : (
                  <Button size="sm" onClick={handleUnlock}>
                    {t("adminClustersDetail.UnlockClusterButton")}
                  </Button>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <AddAdminDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={(adminUserId, password) =>
          addAdmin.mutate(
            { adminUserId, password },
            {
              onSuccess: () => {
                toast.success(t("adminClustersDetail.adminAdded", { adminUserId }));
                setAddOpen(false);
              },
              onError: (e) => toast.error(e.message || t("adminClustersDetail.addAdminFailed") || "Add admin failed"),
            },
          )
        }
        pending={addAdmin.isPending}
        title={t("adminClustersDetail.addAdminTitle")}
        description={t("adminClustersDetail.wrapCskDescription")}
        t={t}
      />

      <AddAdminDialog
        open={bootstrapOpen}
        onClose={() => setBootstrapOpen(false)}
        onSubmit={(adminUserId, password) =>
          addAdmin.mutate(
            { adminUserId, password },
            {
              onSuccess: () => {
                toast.success(t("adminClustersDetail.envelopeEncryptionEnabled"));
                setBootstrapOpen(false);
              },
              onError: (e) => toast.error(e.message || t("adminClustersDetail.enableFailed") || "Enable failed"),
            },
          )
        }
        pending={addAdmin.isPending}
        title={t("adminClustersDetail.enableEnvelopeEncryptionTitle")}
        description={t("adminClustersDetail.deriveArgon2idDescription")}
        t={t}
      />
    </section>
  );
}

function AddAdminDialog({
  open,
  onClose,
  onSubmit,
  pending,
  title,
  description,
  t,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (adminUserId: string, password: string) => void;
  pending: boolean;
  title: string;
  description: string;
  t: ReturnType<typeof useTranslation>["t"];
}) {
  const [adminUserId, setAdminUserId] = useState("");
  const [password, setPassword] = useState("");
  // Reset-on-close via render-time check rather than an effect — the
  // react-hooks/set-state-in-effect rule rejects state writes inside
  // useEffect for "synchronise external systems" use cases like this.
  // Mirrors the pattern in shared/ui/DeleteClusterConfirm.tsx.
  const [openKey, setOpenKey] = useState<boolean>(false);
  if (open && !openKey) {
    setOpenKey(true);
  }
  if (!open && openKey) {
    setOpenKey(false);
    if (adminUserId !== "") setAdminUserId("");
    if (password !== "") setPassword("");
  }

  const submit = () => {
    if (!adminUserId || !password) return;
    onSubmit(adminUserId, password);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
      </DialogHeader>
      <div className="space-y-3 pt-2">
        <p className="text-sm text-muted-foreground">{description}</p>
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">
            {t("adminClustersDetail.adminUserIdLabel")}
          </label>
          <Input
            autoFocus
            value={adminUserId}
            onChange={(e) => setAdminUserId(e.target.value)}
            placeholder="matthew"
            data-testid="add-admin-userid"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">
            {t("adminClustersDetail.passwordLabel")}
          </label>
          <Input
            type="password"
            autoComplete="off"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("adminClustersDetail.clusterAdminPasswordPlaceholder") || "Cluster admin password"}
            data-testid="add-admin-password"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submit();
              }
            }}
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={pending}>
          {t("adminClustersDetail.cancelButton")}
        </Button>
        <Button onClick={submit} disabled={pending || !adminUserId || !password}>
          {pending ? t("adminClustersDetail.savingButton") : t("adminClustersDetail.saveButton")}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
